import { AnyControl, ComplexControl, EnumControl, Fallback, Style, StyleControl, Theme, ValueControl } from "./types";
import { findAllControls, get, isControl, isStyleGroup, resolveChildControls, set } from "./utils";
import { toFontFamilyCSSString } from "./font-family";

type ControlValue = string | number | boolean | null;
export interface BlockStyleValues {
    [key: string]: BlockStyleValues | ControlValue;
}

type ControlValueAssigner<TControl extends StyleControl> = (
    existing: BlockStyleValues,
    control: TControl,
    theme: Theme,
) => void;
type ControlValueAssignerMap<TControl extends StyleControl> = {
    [TType in TControl["controlType"]]: TControl extends { controlType: TType }
        ? ControlValueAssigner<TControl>
        : never;
};
export type ValueOrEnumControl = EnumControl<ControlValue> | ValueControl<ControlValue>;

const assignThemeFallback: ControlValueAssigner<ValueOrEnumControl> = (existing, control, theme) => {
    if (control.fallback.type !== "THEME") {
        throw new Error("assignThemeFallback can only be used on theme fallback.");
    }

    const value = get(theme, control.fallback.key) as ControlValue | undefined;
    if (value === null || value === undefined) {
        return;
    }

    return set(existing, control.key, value);
};

const assignValueFallback: ControlValueAssigner<ValueOrEnumControl> = (existing, control) => {
    if (control.fallback.type !== "VALUE") {
        throw new Error("assignValueFallback can only be used on value fallback.");
    }

    set(existing, control.key, control.fallback.value);
};

const assignerByFallbackType: Record<Fallback["type"], ControlValueAssigner<ValueOrEnumControl>> = {
    THEME: assignThemeFallback,
    VALUE: assignValueFallback,
};

const assignKeyValue: ControlValueAssigner<ValueOrEnumControl> = (existing, control, theme) => {
    if (control.value !== null) {
        return set(existing, control.key, control.value);
    }

    const assignFallback = assignerByFallbackType[control.fallback.type];
    assignFallback(existing, control, theme);
};

const assignComplexValue: ControlValueAssigner<ComplexControl<ValueOrEnumControl>> = (existing, { styles }, theme) => {
    styles.forEach((style) => assignControlValue(existing, style as AnyControl, theme));
};

/**
 * Recursively extracts style values from a list of styles using the provided theme.
 * Used in blocks runtime.
 */
export const extractStyleValues = (
    styles: ReadonlyArray<Style>,
    theme: Theme = {},
    values: BlockStyleValues = {},
): BlockStyleValues => {
    styles.forEach((style) => {
        if (isControl(style)) {
            assignControlValue(values, style, theme);
        }

        if (isStyleGroup(style)) {
            extractStyleValues(style.styles, theme, values);
        }
    });

    return values;
};

const assignControlValue = (existing: BlockStyleValues, control: AnyControl, theme: Theme) => {
    const assigner = controlValueAssignerMap[control.controlType] as ControlValueAssigner<AnyControl> | undefined;
    if (!assigner) throw new Error(`Unknown control type: "${control.controlType}"`);
    assigner(existing, control, theme);
};

const commonControlValueAssignerMap = {
    align: assignKeyValue,
    boolean: assignKeyValue,
    color: assignKeyValue,
    opacity: assignKeyValue,
    padding: assignKeyValue,
    shadow: assignKeyValue,
    size: assignKeyValue,
    width: assignKeyValue,
    height: assignKeyValue,
    roundness: assignKeyValue,
    borderStyle: assignKeyValue,
    borderWidth: assignKeyValue,
    border: assignComplexValue,
    background: assignComplexValue,
    font: assignComplexValue,
    colorPalette: assignKeyValue,
    icon: assignKeyValue,
    image: assignKeyValue,
    fontWeight: assignKeyValue,
    aspectRatio: assignKeyValue,
    position: assignKeyValue,
    placement: assignKeyValue,
    shape: assignKeyValue,
    select: assignKeyValue,
    pixelRange: assignKeyValue,
    radio: assignKeyValue,
} satisfies Partial<ControlValueAssignerMap<AnyControl>>;

const controlValueAssignerMap: ControlValueAssignerMap<AnyControl> = {
    ...commonControlValueAssignerMap,
    fontFamily: (existing, control, theme) => {
        // This assigner is a bit different from the other ones as we need
        // to take care that the final value is a valid CSS font-family string
        // E.g. "Source Serif 4" should become '"Source Serif 4"' but
        // "system-ui" should stay as is without quotes

        if (control.value !== null) {
            const cssValue = toFontFamilyCSSString(control.value);
            return set(existing, control.key, cssValue);
        }

        if (control.fallback.type === "THEME") {
            const value = get(theme, control.fallback.key);
            if (value != null && typeof value !== "string") {
                throw new Error(
                    "Expected font family theme fallback to be a string or null/undefined, but got: " + typeof value,
                );
            }
            if (!value) return;
            // NOTE: value from theme keys MUST already be a valid CSS font-family string
            // since it's used as token throughout various elements. We should never
            // pass it through `toFontFamilyCSSString` here, otherwise you'll end up
            // with invalid font family.
            return set(existing, control.key, value);
        }

        if (control.fallback.type === "VALUE") {
            const cssValue = toFontFamilyCSSString(control.fallback.value);
            return set(existing, control.key, cssValue);
        }

        throw new Error(`Invalid font family fallback type: ${JSON.stringify(control.fallback)}`);
    },
};

/**
 * Studio-only version of extractStyleValues. Should only be used in Studio context
 * where style parsing may differ (e.g., raw font names).
 */
export const extractStyleValuesStudio = (
    styles: ReadonlyArray<Style>,
    theme: Theme = {},
    values: BlockStyleValues = {},
): BlockStyleValues => {
    styles.forEach((style) => {
        if (isControl(style)) {
            assignControlValueStudio(values, style, theme);
        }

        if (isStyleGroup(style)) {
            extractStyleValuesStudio(style.styles, theme, values);
        }
    });

    return values;
};

const assignControlValueStudio = (existing: BlockStyleValues, control: AnyControl, theme: Theme) => {
    const assigner = studioControlValueAssignerMap[control.controlType] as ControlValueAssigner<AnyControl> | undefined;
    if (!assigner) throw new Error(`Unknown control type: "${control.controlType}"`);
    assigner(existing, control, theme);
};

const studioControlValueAssignerMap: ControlValueAssignerMap<AnyControl> = {
    ...commonControlValueAssignerMap,
    fontFamily: assignKeyValue,
};

export const extractParentChildStyles = (
    parentStyles: ReadonlyArray<Style>,
    childStyles: ReadonlyArray<Style>,
    theme: Theme = {},
): BlockStyleValues => {
    const parentControls = findAllControls(parentStyles);
    const childControls = resolveChildControls(parentControls, findAllControls(childStyles));
    const parentStyleValues = extractStyleValues(parentControls, theme);
    return extractStyleValues(childControls, theme, parentStyleValues);
};
